import { motion } from "framer-motion";
import heroImage from "@/assets/hero-product.webp";
import heroSingleBottles from "@/assets/hero-single-bottles.webp";
import heroSet4 from "@/assets/hero-set-4.webp";
import heroCourse12 from "@/assets/hero-course-12.webp";

const productVariants = [
  {
    image: heroSingleBottles,
    label: "1 флакон",
    sublabel: "4 варианта насадок",
    alt: "Купить ЖИВАРУС флакон 100 мл — крышка, пипетка, спрей, мерный стаканчик. Натуральный фитопрепарат FARMASVET",
  },
  {
    image: heroSet4,
    label: "Комплект из 4",
    sublabel: "Подарочная упаковка",
    alt: "Купить ЖИВАРУС комплект 4 флакона в подарочной упаковке — биопрепарат для иммунитета FARMASVET",
  },
  {
    image: heroCourse12,
    label: "Курс из 12",
    sublabel: "Премиальная коробка",
    alt: "Купить ЖИВАРУС курс 12 флаконов в премиальной коробке — полный курс восстановления здоровья FARMASVET",
  },
];

const HeroSection = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" aria-label="ЖИВАРУС — купить натуральный биопрепарат нового поколения от FARMASVET с доставкой по России">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="ЖИВАРУС (ZHIVARUS) — натуральный биопрепарат нового поколения из 300+ компонентов, 33 лекарственных травы, 7 коллоидных металлов и 5 эфирных масел от компании FARMASVET серия Фарма-Вита"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      </div>

      <div className="container mx-auto relative z-10 px-4 pt-24 pb-16">
        {/* Top content */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-accent/60 backdrop-blur-sm mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs font-body tracking-[0.2em] text-primary uppercase">
              FARMASVET · Серия «Фарма-Вита»
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold leading-[1.1] mb-6"
          >
            <span className="text-foreground">Биопрепарат</span>
            <br />
            <span className="text-gradient-gold">«Живарус»</span>
          </motion.h1>

          <p
            className="hero-description text-lg text-muted-foreground font-body leading-relaxed mb-8 max-w-2xl mx-auto"
          >
            Двухкомпонентная система нового поколения от компании FARMASVET — линейка «Фарма-Вита».
            Натуральный растительный экстракт из 300+ компонентов: 33 лекарственных травы, 7 коллоидных металлов
            (золото, серебро, медь, платина, цинк, селен, магний), 5 эфирных масел и фуллерены C₆₀.
            Антимикробный комплекс биологической защиты. Укрепление иммунитета, детоксикация, регенерация клеток.
            Купить ЖИВАРУС с доставкой по всей России.
          </p>
        </div>

        {/* Product variants grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12"
        >
          {productVariants.map((variant, index) => (
            <motion.div
              key={variant.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.15 }}
              className="group text-center"
            >
              <div className="relative rounded-lg p-4 bg-background/30 backdrop-blur-sm border border-foreground/10 hover:border-primary/40 transition-colors duration-300">
                <img
                  src={variant.image}
                  alt={variant.alt}
                  width={1024}
                  height={768}
                  className="w-full h-auto object-contain drop-shadow-lg group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
              <h2 className="mt-3 text-lg font-display font-semibold text-foreground">
                {variant.label}
              </h2>
              <p className="text-sm text-muted-foreground font-body">
                {variant.sublabel}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA + highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="flex flex-wrap justify-center gap-4 mb-8"
        >
          <a
            href="#order"
            className="inline-flex items-center px-8 py-3.5 rounded-sm bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:opacity-90 transition-opacity"
          >
            Купить ЖИВАРУС
          </a>
          <a
            href="#about"
            className="inline-flex items-center px-8 py-3.5 rounded-sm border border-foreground/20 text-foreground font-body font-medium tracking-wide hover:border-primary hover:text-primary transition-colors"
          >
            Узнать о составе
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="flex flex-wrap justify-center gap-6 text-sm font-body"
        >
          <div className="flex items-center gap-2">
            <span className="text-primary">✦</span>
            <span className="text-muted-foreground">300+ натуральных компонентов</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary">✦</span>
            <span className="text-muted-foreground">7 коллоидных металлов</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary">✦</span>
            <span className="text-muted-foreground">5 эфирных масел</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary">✦</span>
            <span className="text-muted-foreground">Фуллерены C₆₀</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary">✦</span>
            <span className="text-muted-foreground">Антимикробный биощит</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
